import React, { useState, useEffect, useCallback } from "react";
import { camelCase } from "lodash";
import { useLocation } from "react-router-dom";
import AppSidebar from "../components/shared/sidebar/Sidebar";
import { SidebarProvider } from "../components/ui/sidebar";
import Header from "../components/shared/Header";
import { Toaster } from "sonner";
import { useOrganizationStore } from "../stores/useOrganizationStore";
import { useAuthStore } from "../stores/useAuthStore";
import useAvailableFacilityComponents from "../data/hooks/useAvailableFacilityComponents";
import { useWidgetCatalog } from "../data/hooks/custom-dashboard";
import { useFleetOutageStream } from "../data/hooks/useFleetOutageStream";
import { useCurrentRole } from "../data/hooks/useCurrentRole";
import { isPlatformAdmin } from "../lib/rbac";
import type { ComponentKey } from "../data/types";
import { COMPONENT_LABEL_MAP } from "../lib/constants";
import { SidebarContext } from "../contexts/SidebarContext";

type AppLayoutProps = {
  children: React.ReactNode;
};

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { pathname } = useLocation();

  const activeFacilityId = useOrganizationStore((store) => store.activeFacilityId);
  const activeOrgId = useOrganizationStore((store) => store.activeOrgId);
  const setActiveFacility = useOrganizationStore((store) => store.setActiveFacility);
  const setActiveOrg = useOrganizationStore((store) => store.setActiveOrg);
  const setAvailableComponents = useOrganizationStore((store) => store.setAvailableComponents);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Keep the store in sync with the URL so that navigating via sidebar links or
  // direct URL changes always drives the correct org/facility into the store.
  useEffect(() => {
    const match = pathname.match(/\/organizations\/(\d+)\/facilities\/(\d+)/);
    if (!match) return;
    const urlOrgId = Number(match[1]);
    const urlFacilityId = Number(match[2]);
    if (urlOrgId !== activeOrgId) setActiveOrg(urlOrgId);
    // Always sync the facility explicitly — setActiveOrg auto-selects the
    // first facility which may differ from the one in the URL.
    if (urlFacilityId !== activeFacilityId) setActiveFacility(urlFacilityId);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear stale components immediately when the active facility changes so
  // consumers never see components belonging to the previous facility.
  useEffect(() => {
    setAvailableComponents([]);
  }, [activeFacilityId, setAvailableComponents]);

  // Gated on isAuthenticated — AppLayout mounts outside RequireAuth's
  // per-route boot gate, so without this these fire immediately on reload,
  // before a valid access token exists, causing avoidable 401s and extra
  // refresh calls.
  const { data: availableComponents } = useAvailableFacilityComponents(activeFacilityId, isAuthenticated);

  // Prefetch widget catalog in the background so it's warm before the user
  // opens the custom dashboard builder. staleTime: Infinity means this fires
  // once per session and is never re-fetched automatically.
  useWidgetCatalog({ enabled: isAuthenticated });

  // Session-long, app-shell-mounted admin outage connection (§2.3 of the
  // FE push architecture spec) — not scoped to any route, unlike the
  // per-facility connection. Gated on platform-admin role so non-admin
  // sessions never open the connection or fire its initial REST fetch.
  const role = useCurrentRole();
  useFleetOutageStream(isPlatformAdmin(role));

  // Normalize API response (snake_case → camelCase) and drop any keys not in
  // COMPONENT_LABEL_MAP before pushing into the store.
  useEffect(() => {
    if (availableComponents?.componentTypes) {
      const camelCasedComponents = availableComponents.componentTypes
        .map((c) => camelCase(c))
        .filter((component): component is ComponentKey => component in COMPONENT_LABEL_MAP);
      setAvailableComponents(camelCasedComponents);
    }
  }, [availableComponents, setAvailableComponents]);

  const collapseSidebar = useCallback(() => setIsCollapsed(true), []);

  return (
    <SidebarContext.Provider value={{ collapseSidebar }}>
      <div
        className={`h-screen w-full md:grid transition-all duration-300 ${isCollapsed
          ? "md:grid-cols-[64px_1fr]"
          : "md:grid-cols-[280px_1fr]"
          }`}
      >
        <SidebarProvider>
          <div className="h-full overflow-hidden">
            <AppSidebar
              isCollapsed={isCollapsed}
              onCollapsedChange={setIsCollapsed}
            />
          </div>
        </SidebarProvider>

        <div className="flex flex-col h-full overflow-hidden min-w-0">
          <Header />
          <main className="flex-1 overflow-y-auto px-2 min-w-0">
            {children}
          </main>
        </div>

        <Toaster position="top-right" richColors />
      </div>
    </SidebarContext.Provider>
  );
};

export default AppLayout;